import { useRef, useState, type DragEvent as ReactDragEvent } from "react";

import { postDemoPlotRecall, type DemoV3PlotRecallResult } from "../../../api/demoV3";

/**
 * Plot recall (VERTICAL_DEMOS_V2 D4): drop a (possibly degraded) plot
 * screenshot and recover the exact live artifact it came from — perceptual
 * fingerprint match against the project's registered rendered plots in
 * thread_core, then one click into the artifact's full lineage.
 */

function matchClass(match: DemoV3PlotRecallResult["match"]): string {
  return match === "definitive" ? "is-interior" : match === "ambiguous" ? "is-on-hull" : "is-extrapolated";
}

export function PlotRecallCard({
  projectId,
  onOpenArtifact,
}: {
  projectId: string;
  branch?: string;
  state?: Record<string, unknown>;
  onOpenArtifact?: (artifactId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [result, setResult] = useState<DemoV3PlotRecallResult | null>(null);
  const [matching, setMatching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const submit = (file: File | null | undefined) => {
    if (!file || matching) return;
    setMatching(true);
    setError(null);
    setResult(null);
    postDemoPlotRecall(projectId, file)
      .then(setResult)
      .catch((err: Error) => {
        // 501: recall not wired for this deployment; 404: no registered plots.
        if (err.message.startsWith("API 501")) setUnavailable("Plot recall is not available on this deployment.");
        else setError(err.message.replace(/^API \d+: /, ""));
      })
      .finally(() => setMatching(false));
  };

  const handleDrop = (event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    submit(event.dataTransfer.files?.[0]);
  };

  if (unavailable) {
    return (
      <div className="v5-demo-card is-empty v5-plot-recall is-disabled" data-tour="v5-plot-recall" data-testid="v5-plot-recall">
        <strong>Plot recall</strong>
        <span>{unavailable}</span>
      </div>
    );
  }

  return (
    <div className="v5-demo-card v5-plot-recall" data-tour="v5-plot-recall" data-testid="v5-plot-recall">
      <div
        className={`v5-plot-recall-drop ${dragOver ? "is-drag-over" : ""}`}
        role="button"
        tabIndex={0}
        data-testid="v5-plot-recall-drop"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
        onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {matching
          ? "Matching fingerprint…"
          : "Drop a plot screenshot (or click to pick one) — even cropped, rescaled or JPEG-mangled — and recover the live artifact it came from."}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="v5-plot-recall-input"
          onChange={(event) => { submit(event.target.files?.[0]); event.target.value = ""; }}
        />
      </div>

      {error ? <div className="v5-demo-hint is-error" data-testid="v5-plot-recall-error">{error}</div> : null}

      {result ? (
        <div className="v5-plot-recall-result" data-testid="v5-plot-recall-result">
          <span className={`v5-chip v5-demo-status-chip ${matchClass(result.match)}`}>
            {result.match === "definitive" ? "Definitive match" : result.match === "ambiguous" ? "Ambiguous" : "No match"}
          </span>
          {result.confidence !== null ? (
            <span className="v5-demo-footnote">confidence {(result.confidence * 100).toFixed(0)}% · distance {result.distance}</span>
          ) : null}
          {result.recovered_artifact_id ? (
            <>
              <code className="v5-plot-recall-id" title={result.recovered_artifact_id}>
                {result.plot ?? "artifact"} · {result.recovered_artifact_id.slice(0, 8)}…
              </code>
              <button
                type="button"
                className="v5-primary-button"
                data-testid="v5-plot-recall-open"
                onClick={() => onOpenArtifact?.(result.recovered_artifact_id as string)}
              >
                Open artifact
              </button>
            </>
          ) : (
            <span className="v5-demo-hint">
              {result.match === "ambiguous"
                ? "Two candidates are too close to call — no artifact resolved."
                : "Nothing in this project matches that image."}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
